import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiPost, apiGet } from '../services/api'
import { useAuthStore } from '../hooks/useAuth'
import {
  Video, Users, MessageSquare, Mic, MicOff, VideoOff,
  PhoneOff, Share, MoreVertical, Clock, Calendar,
  ArrowLeft, Copy, CheckCircle
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Meeting {
  id: string
  title: string
  meetLink: string
  scheduledAt: string
  duration: number
  participants: number
  status: 'scheduled' | 'live' | 'ended'
  host: string
}

export default function LiveClass() {
  const { id } = useParams()
  const { user } = useAuthStore()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newMeetingTitle, setNewMeetingTitle] = useState('')
  const [newMeetingTime, setNewMeetingTime] = useState('')

  const meetings: Meeting[] = [
    {
      id: '1', title: 'Advanced Math Live Session',
      meetLink: 'https://meet.google.com/abc-defg-hij',
      scheduledAt: '2026-05-05T14:00:00',
      duration: 60, participants: 24, status: 'live',
      host: 'Dr. Sarah Johnson'
    },
    {
      id: '2', title: 'Python Programming Workshop',
      meetLink: 'https://meet.google.com/klm-nopq-rst',
      scheduledAt: '2026-05-06T10:00:00',
      duration: 90, participants: 0, status: 'scheduled',
      host: 'Alex Chen'
    },
    {
      id: '3', title: 'Physics Q&A Session',
      meetLink: 'https://meet.google.com/uvw-xyz1-234',
      scheduledAt: '2026-05-04T15:00:00',
      duration: 45, participants: 18, status: 'ended',
      host: 'Prof. Michael Brown'
    },
  ]

  const handleCreateMeeting = async () => {
    try {
      // In real app: const data = await apiPost('/meetings/create', { title: newMeetingTitle, scheduledAt: newMeetingTime })
      toast.success('Meeting created! Google Meet link generated.')
      setShowCreateModal(false)
      setNewMeetingTitle('')
      setNewMeetingTime('')
    } catch {
      toast.error('Failed to create meeting')
    }
  }

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link)
    toast.success('Meet link copied!')
  }

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'live': return 'bg-danger/20 text-danger animate-pulse'
      case 'scheduled': return 'bg-warning/20 text-warning'
      case 'ended': return 'bg-slate-700 text-slate-400'
      default: return 'bg-slate-700 text-slate-400'
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Live Classes</h1>
          <p className="text-slate-500 mt-1">Google Meet integrated video sessions</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'teacher') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          >
            <Video className="w-4 h-4" />
            Create Meeting
          </button>
        )}
      </div>

      {/* Active Live Session Banner */}
      {meetings.some(m => m.status === 'live') && (
        <div className="glass rounded-xl p-4 border border-danger/30 bg-danger/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-danger rounded-full animate-pulse" />
              <div>
                <h3 className="font-semibold">🔴 LIVE NOW: {meetings.find(m => m.status === 'live')?.title}</h3>
                <p className="text-sm text-slate-500">
                  Hosted by {meetings.find(m => m.status === 'live')?.host} • 
                  {meetings.find(m => m.status === 'live')?.participants} participants
                </p>
              </div>
            </div>
            <a
              href={meetings.find(m => m.status === 'live')?.meetLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 bg-danger hover:bg-danger/80 rounded-lg font-medium transition-colors"
            >
              Join Now
            </a>
          </div>
        </div>
      )}

      {/* Meetings List */}
      <div className="space-y-4">
        {meetings.map(meeting => (
          <div key={meeting.id} className="glass rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  meeting.status === 'live' ? 'bg-danger/20' : 'bg-primary/20'
                }`}>
                  <Video className={`w-6 h-6 ${
                    meeting.status === 'live' ? 'text-danger' : 'text-primary'
                  }`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{meeting.title}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(meeting.status)}`}>
                      {meeting.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-2">Hosted by {meeting.host}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(meeting.scheduledAt).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {meeting.duration} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {meeting.participants} joined
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {meeting.status === 'live' && (
                  <a
                    href={meeting.meetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-danger hover:bg-danger/80 rounded-lg text-sm font-medium transition-colors"
                  >
                    Join Live
                  </a>
                )}
                {meeting.status === 'scheduled' && (
                  <button
                    onClick={() => copyLink(meeting.meetLink)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Copy Link
                  </button>
                )}
                {(user?.role === 'admin' || user?.role === 'teacher') && (
                  <button className="p-2 hover:bg-slate-800 rounded-lg">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Meet Link Display */}
            {meeting.status !== 'ended' && (
              <div className="mt-3 p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                <code className="text-sm text-slate-400">{meeting.meetLink}</code>
                <button
                  onClick={() => copyLink(meeting.meetLink)}
                  className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Meeting Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create Google Meet</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Meeting Title</label>
                <input
                  type="text"
                  value={newMeetingTitle}
                  onChange={(e) => setNewMeetingTitle(e.target.value)}
                  placeholder="e.g., Math Live Session"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Schedule Time</label>
                <input
                  type="datetime-local"
                  value={newMeetingTime}
                  onChange={(e) => setNewMeetingTime(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMeeting}
                  disabled={!newMeetingTitle || !newMeetingTime}
                  className="flex-1 py-2.5 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Create & Generate Meet Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
